import React, { useState, useEffect, useCallback } from 'react'
import { getErrorMessage } from '../../utils/api'

/* ─── Types ─────────────────────────────────────────────────────────────── */

type ScheduleType = 'instant' | 'scheduled' | 'recurring'
interface RecurWindow { sd: number; st: string; ed: number; et: string }
interface Recurrence { tz: string; windows: RecurWindow[] }

interface WidgetNotice {
  id: string
  title: string
  body: string
  style: 'info' | 'warning' | 'urgent'
  is_active: number
  priority: number
  schedule_type: ScheduleType
  start_date: string | null
  end_date: string | null
  recurrence: Recurrence | string | null
  mute_alerts: boolean | number | null
  created_by: string | null
  created_at: string
  updated_at: string
}

interface NoticeForm {
  title: string
  body: string
  style: 'info' | 'warning' | 'urgent'
  priority: number
  schedule_type: ScheduleType
  start_date: string
  end_date: string
  recurrence: Recurrence
  mute_alerts: boolean
}

const DEFAULT_TZ = 'Europe/London'

const EMPTY_FORM: NoticeForm = {
  title: '', body: '', style: 'info', priority: 0,
  schedule_type: 'instant', start_date: '', end_date: '',
  recurrence: { tz: DEFAULT_TZ, windows: [] },
  mute_alerts: false,
}

/* ─── Recurring-schedule helpers (independent of the WA-notice copy) ───────── */

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const TZ_OPTIONS = ['Europe/London', 'Europe/Dublin', 'UTC', 'America/New_York', 'America/Los_Angeles', 'Australia/Sydney']

const SCHEDULE_PRESETS: { key: string; label: string; windows: RecurWindow[] }[] = [
  { key: 'weekend', label: 'Every weekend (Fri 5pm → Mon 9am)', windows: [{ sd: 5, st: '17:00', ed: 1, et: '09:00' }] },
  {
    key: 'weeknights', label: 'Weeknights (Mon–Fri 6pm → 8am)', windows: [
      { sd: 1, st: '18:00', ed: 2, et: '08:00' }, { sd: 2, st: '18:00', ed: 3, et: '08:00' },
      { sd: 3, st: '18:00', ed: 4, et: '08:00' }, { sd: 4, st: '18:00', ed: 5, et: '08:00' },
    ],
  },
  { key: 'weekends_full', label: 'All weekend (Sat & Sun, all day)', windows: [{ sd: 6, st: '00:00', ed: 1, et: '00:00' }] },
]

function to12h(hhmm: string): string {
  const [h, m] = (hhmm || '00:00').split(':').map(Number)
  const ap = h < 12 ? 'AM' : 'PM'
  const h12 = ((h + 11) % 12) + 1
  return `${h12}:${String(m || 0).padStart(2, '0')} ${ap}`
}
function summarizeWindows(windows: RecurWindow[]): string {
  if (!windows.length) return 'No windows yet — add one (or pick a preset) so the banner knows when to show.'
  return windows.map(w => `${DAY_NAMES[w.sd]} ${to12h(w.st)} → ${DAY_NAMES[w.ed]} ${to12h(w.et)}`).join(' · ')
}
function parseRecurrence(input: Recurrence | string | null): Recurrence {
  if (!input) return { tz: DEFAULT_TZ, windows: [] }
  let rec: any = input
  if (typeof input === 'string') { try { rec = JSON.parse(input) } catch { return { tz: DEFAULT_TZ, windows: [] } } }
  const windows = Array.isArray(rec?.windows) ? rec.windows.map((w: any) => ({ sd: Number(w.sd), st: String(w.st || '00:00'), ed: Number(w.ed), et: String(w.et || '00:00') })) : []
  return { tz: typeof rec?.tz === 'string' && rec.tz ? rec.tz : DEFAULT_TZ, windows }
}

const STYLE_CONFIG = {
  info: {
    label: 'Info',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    desc: 'General information — holiday hours, planned maintenance',
  },
  warning: {
    label: 'Warning',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    desc: 'Reduced availability — limited agents, slow response times',
  },
  urgent: {
    label: 'Urgent',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    desc: 'No service — all agents unavailable, system outage',
  },
}

/* ─── Icons ──────────────────────────────────────────────────────────────── */

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
)

const PencilIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
  </svg>
)

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
  </svg>
)

const XIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
)

const BellIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
  </svg>
)

/* ─── Component ──────────────────────────────────────────────────────────── */

export const WidgetNoticesSettings: React.FC = () => {
  const [notices, setNotices] = useState<WidgetNotice[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingNotice, setEditingNotice] = useState<WidgetNotice | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [form, setForm] = useState<NoticeForm>(EMPTY_FORM)

  const getToken = () => localStorage.getItem('auth_token') || ''

  const showNotification = (msg: string, type: 'success' | 'error') => {
    if (type === 'success') { setSuccess(msg); setError(null) }
    else { setError(msg); setSuccess(null) }
    setTimeout(() => { setSuccess(null); setError(null) }, 4000)
  }

  const fetchNotices = useCallback(async () => {
    try {
      const res = await fetch('/api/widget-notices', {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      if (!res.ok) throw new Error('Failed to fetch notices')
      const data = await res.json()
      setNotices(data.notices || [])
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load notices'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchNotices() }, [fetchNotices])

  const openCreate = () => {
    setEditingNotice(null)
    setForm(EMPTY_FORM)
    setShowForm(true)
  }

  const openEdit = (notice: WidgetNotice) => {
    setEditingNotice(notice)
    setForm({
      title: notice.title, body: notice.body, style: notice.style, priority: notice.priority,
      schedule_type: notice.schedule_type || 'instant',
      start_date: notice.start_date ? String(notice.start_date).slice(0, 10) : '',
      end_date: notice.end_date ? String(notice.end_date).slice(0, 10) : '',
      recurrence: parseRecurrence(notice.recurrence),
      mute_alerts: !!notice.mute_alerts,
    })
    setShowForm(true)
  }

  const handleSubmit = async () => {
    // The message is optional — the widget shows a built-in "team unavailable"
    // message during notice hours, so only a title is required.
    if (!form.title.trim()) {
      showNotification('A title is required', 'error')
      return
    }
    if (form.schedule_type === 'recurring' && form.recurrence.windows.length === 0) {
      showNotification('Add at least one recurring window (or pick a preset)', 'error')
      return
    }

    try {
      const url = editingNotice ? `/api/widget-notices/${editingNotice.id}` : '/api/widget-notices'
      const method = editingNotice ? 'PUT' : 'POST'
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(form),
      })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(typeof data.error === 'string' ? data.error : (typeof data.message === 'string' ? data.message : 'Failed to save notice'))
      }
      showNotification(editingNotice ? 'Notice updated' : 'Notice created', 'success')
      setShowForm(false)
      setEditingNotice(null)
      await fetchNotices()
    } catch (err) {
      showNotification(getErrorMessage(err, 'Failed to save'), 'error')
    }
  }

  const handleToggle = async (notice: WidgetNotice) => {
    try {
      const res = await fetch(`/api/widget-notices/${notice.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ is_active: notice.is_active ? false : true }),
      })
      if (!res.ok) throw new Error('Failed to toggle notice')
      await fetchNotices()
      showNotification(
        notice.is_active ? `"${notice.title}" deactivated` : `"${notice.title}" is now live`,
        'success'
      )
    } catch (err) {
      showNotification(getErrorMessage(err, 'Toggle failed'), 'error')
    }
  }

  const handleDelete = async (notice: WidgetNotice) => {
    if (!window.confirm(`Delete "${notice.title}"? This cannot be undone.`)) return
    try {
      const res = await fetch(`/api/widget-notices/${notice.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      if (!res.ok) throw new Error('Failed to delete')
      showNotification('Notice deleted', 'success')
      await fetchNotices()
    } catch (err) {
      showNotification(getErrorMessage(err, 'Delete failed'), 'error')
    }
  }

  /* ─── Render ──────────────────────────────────────────────────────────── */

  return (
    <div>
      {/* Section Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BellIcon />
          <h3 className="text-lg font-semibold text-txt-primary">Widget Notices</h3>
          <span className="text-xs text-txt-tertiary">
            ({notices.filter(n => n.is_active).length} active)
          </span>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-3 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors duration-150 shadow-sm"
        >
          <PlusIcon />
          New Notice
        </button>
      </div>

      <p className="text-sm text-txt-secondary mb-5">
        Create notification banners that appear at the top of the chat widget. Toggle them on or off as needed — perfect for holidays, outages, or reduced hours.
      </p>

      {/* Notifications */}
      {(error || success) && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium animate-slide-down ${
          error
            ? 'bg-error-light text-error dark:bg-red-900/30 dark:text-red-300'
            : 'bg-success-light text-success dark:bg-green-900/30 dark:text-green-300'
        }`}>
          {error || success}
        </div>
      )}

      {/* Notices List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin-slow" />
        </div>
      ) : notices.length === 0 ? (
        <div className="text-center py-12 text-txt-tertiary">
          <BellIcon />
          <p className="mt-2 text-sm">No notices yet. Create one to display in the chat widget.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {notices.map(notice => (
            <div
              key={notice.id}
              className={`bg-surface rounded-xl border overflow-hidden shadow-sm transition-all duration-150 ${
                notice.is_active ? 'border-brand-300 dark:border-brand-700' : 'border-border opacity-75'
              }`}
            >
              <div className="flex items-start gap-4 p-4">
                {/* Toggle */}
                <button
                  onClick={() => handleToggle(notice)}
                  style={{
                    marginTop: '2px',
                    position: 'relative',
                    display: 'inline-flex',
                    height: '24px',
                    width: '44px',
                    flexShrink: 0,
                    cursor: 'pointer',
                    borderRadius: '9999px',
                    border: '2px solid transparent',
                    transition: 'background-color 200ms',
                    backgroundColor: notice.is_active ? 'var(--color-brand-600, #2563eb)' : '#d1d5db',
                  }}
                  aria-pressed={!!notice.is_active}
                  role="switch"
                  title={notice.is_active ? 'Click to deactivate' : 'Click to activate'}
                >
                  <span
                    style={{
                      pointerEvents: 'none',
                      display: 'inline-block',
                      height: '20px',
                      width: '20px',
                      borderRadius: '9999px',
                      backgroundColor: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      transition: 'transform 200ms',
                      transform: notice.is_active ? 'translateX(20px)' : 'translateX(0)',
                    }}
                  />
                </button>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm text-txt-primary truncate">{notice.title}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STYLE_CONFIG[notice.style].badge}`}>
                      {STYLE_CONFIG[notice.style].label}
                    </span>
                    {notice.is_active ? (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                        Live
                      </span>
                    ) : null}
                  </div>
                  <p className="text-sm text-txt-secondary line-clamp-2">{notice.body}</p>
                  {notice.schedule_type === 'scheduled' && notice.start_date && (
                    <p className="text-xs text-txt-tertiary mt-0.5">📅 {String(notice.start_date).slice(0, 10)} — {notice.end_date ? String(notice.end_date).slice(0, 10) : '…'}</p>
                  )}
                  {notice.schedule_type === 'recurring' && (
                    <p className="text-xs text-txt-tertiary mt-0.5">🔁 {summarizeWindows(parseRecurrence(notice.recurrence).windows)}</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => openEdit(notice)}
                    className="p-2 rounded-lg text-txt-tertiary hover:text-brand-600 hover:bg-surface-hover transition-colors"
                    title="Edit"
                  >
                    <PencilIcon />
                  </button>
                  <button
                    onClick={() => handleDelete(notice)}
                    className="p-2 rounded-lg text-txt-tertiary hover:text-error hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="Delete"
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>

              {/* Preview bar */}
              {!!notice.is_active && (
                <div className={`px-4 py-2 text-xs border-t ${
                  notice.style === 'info'
                    ? 'bg-blue-50 text-blue-700 border-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800'
                    : notice.style === 'warning'
                    ? 'bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800'
                    : 'bg-red-50 text-red-700 border-red-100 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800'
                }`}>
                  Showing in widget now
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
          <div className="bg-surface rounded-2xl shadow-xl w-full max-w-lg border border-border animate-slide-down flex flex-col max-h-[85vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
              <h3 className="text-lg font-semibold text-txt-primary">
                {editingNotice ? 'Edit Notice' : 'Create Notice'}
              </h3>
              <button
                onClick={() => { setShowForm(false); setEditingNotice(null) }}
                className="p-1 rounded-lg text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover transition-colors"
              >
                <XIcon />
              </button>
            </div>

            {/* Modal Body — scrolls within the capped modal height */}
            <div className="px-6 py-5 space-y-5 flex-1 overflow-y-auto">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Bank Holiday Closure"
                  maxLength={100}
                  className="w-full px-3 py-2.5 rounded-lg bg-surface-secondary border border-border text-sm text-txt-primary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors"
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">
                  Message <span className="text-txt-tertiary font-normal">(optional)</span>
                </label>
                <p className="text-xs text-txt-tertiary mb-1.5">
                  Leave blank to use the built-in “our team is currently unavailable” message, which also shows when you’ll be back online.
                </p>
                <textarea
                  value={form.body}
                  onChange={e => setForm({ ...form, body: e.target.value })}
                  placeholder="Optional — overrides the built-in unavailable message."
                  maxLength={500}
                  rows={3}
                  className="w-full px-3 py-2.5 rounded-lg bg-surface-secondary border border-border text-sm text-txt-primary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors resize-none"
                />
                <p className="text-xs text-txt-tertiary mt-1">{form.body.length}/500</p>
              </div>

              {/* Style */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">Style</label>
                <div className="grid grid-cols-3 gap-2">
                  {(Object.keys(STYLE_CONFIG) as Array<keyof typeof STYLE_CONFIG>).map(key => (
                    <button
                      key={key}
                      onClick={() => setForm({ ...form, style: key })}
                      className={`px-3 py-2.5 rounded-lg border text-sm font-medium text-center transition-all duration-150 ${
                        form.style === key
                          ? key === 'info'
                            ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                            : key === 'warning'
                            ? 'bg-amber-50 border-amber-300 text-amber-700 dark:bg-amber-900/30 dark:border-amber-600 dark:text-amber-300'
                            : 'bg-red-50 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-600 dark:text-red-300'
                          : 'bg-surface-secondary border-border text-txt-secondary hover:border-txt-tertiary'
                      }`}
                    >
                      {STYLE_CONFIG[key].label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-txt-tertiary mt-1.5">{STYLE_CONFIG[form.style].desc}</p>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">Priority</label>
                <input
                  type="number"
                  value={form.priority}
                  onChange={e => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
                  min={0}
                  max={99}
                  className="w-24 px-3 py-2.5 rounded-lg bg-surface-secondary border border-border text-sm text-txt-primary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors"
                />
                <p className="text-xs text-txt-tertiary mt-1">Higher priority notices appear first. 0 = default.</p>
              </div>

              {/* Mute alerts */}
              <div className="flex items-start gap-3 rounded-lg border border-border bg-surface-secondary p-3">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, mute_alerts: !form.mute_alerts })}
                  role="switch"
                  aria-checked={form.mute_alerts}
                  style={{
                    marginTop: '2px',
                    position: 'relative',
                    display: 'inline-flex',
                    height: '24px',
                    width: '44px',
                    flexShrink: 0,
                    cursor: 'pointer',
                    borderRadius: '9999px',
                    border: '2px solid transparent',
                    transition: 'background-color 200ms',
                    backgroundColor: form.mute_alerts ? 'var(--color-brand-600, #2563eb)' : '#d1d5db',
                  }}
                >
                  <span
                    style={{
                      pointerEvents: 'none',
                      display: 'inline-block',
                      height: '20px',
                      width: '20px',
                      borderRadius: '9999px',
                      backgroundColor: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      transition: 'transform 200ms',
                      transform: form.mute_alerts ? 'translateX(20px)' : 'translateX(0)',
                    }}
                  />
                </button>
                <label
                  className="text-sm text-txt-primary cursor-pointer select-none"
                  onClick={() => setForm({ ...form, mute_alerts: !form.mute_alerts })}
                >
                  Mute chat sound &amp; notifications while this notice is active
                  <span className="block text-xs text-txt-tertiary mt-0.5">
                    Agents still see new chats in the queue — only the chime and pop-up alerts are silenced.
                  </span>
                </label>
              </div>

              {/* Schedule */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">When to show</label>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    ['instant', 'Always (on/off)'],
                    ['scheduled', 'Date range'],
                    ['recurring', 'Recurring'],
                  ] as [ScheduleType, string][]).map(([val, lbl]) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setForm({ ...form, schedule_type: val })}
                      className={`px-3 py-2.5 rounded-lg border text-sm font-medium text-center transition-all duration-150 ${
                        form.schedule_type === val
                          ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                          : 'bg-surface-secondary border-border text-txt-secondary hover:border-txt-tertiary'
                      }`}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-txt-tertiary mt-1.5">
                  {form.schedule_type === 'instant'
                    ? 'Shows whenever the notice is toggled on, until you turn it off.'
                    : form.schedule_type === 'scheduled'
                    ? 'Shows only between a start and end date (while toggled on).'
                    : 'Shows on a repeating weekly schedule (while toggled on) — e.g. every weekend.'}
                </p>
              </div>

              {/* Date range */}
              {form.schedule_type === 'scheduled' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-txt-secondary mb-1.5">Start Date</label>
                    <input
                      type="date"
                      value={form.start_date}
                      onChange={e => setForm({ ...form, start_date: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-lg bg-surface-secondary border border-border text-sm text-txt-primary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-txt-secondary mb-1.5">End Date</label>
                    <input
                      type="date"
                      value={form.end_date}
                      onChange={e => setForm({ ...form, end_date: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-lg bg-surface-secondary border border-border text-sm text-txt-primary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors"
                    />
                  </div>
                </div>
              )}

              {/* Recurring weekly windows */}
              {form.schedule_type === 'recurring' && (
                <div className="rounded-lg border border-border bg-surface-secondary p-4 space-y-5">
                  <div>
                    <span className="block text-xs font-medium text-txt-secondary mb-1.5">Quick presets</span>
                    <div className="flex flex-wrap gap-1.5">
                      {SCHEDULE_PRESETS.map(p => (
                        <button
                          key={p.key}
                          type="button"
                          onClick={() => setForm({ ...form, recurrence: { ...form.recurrence, windows: p.windows.map(w => ({ ...w })) } })}
                          className="px-2.5 py-1 rounded-full border border-border text-xs font-medium text-txt-secondary hover:border-brand-400 hover:text-brand-600 transition-colors"
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    {form.recurrence.windows.map((w, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-2 py-1 text-sm">
                        <span className="text-txt-tertiary text-xs w-10">From</span>
                        <select
                          value={w.sd}
                          onChange={e => { const ws = [...form.recurrence.windows]; ws[i] = { ...ws[i], sd: Number(e.target.value) }; setForm({ ...form, recurrence: { ...form.recurrence, windows: ws } }) }}
                          className="px-2 py-1.5 rounded-md bg-surface border border-border text-sm text-txt-primary outline-none focus:border-brand-500"
                        >
                          {DAY_NAMES.map((d, di) => <option key={di} value={di}>{d}</option>)}
                        </select>
                        <input
                          type="time"
                          value={w.st}
                          onChange={e => { const ws = [...form.recurrence.windows]; ws[i] = { ...ws[i], st: e.target.value }; setForm({ ...form, recurrence: { ...form.recurrence, windows: ws } }) }}
                          className="px-2 py-1.5 rounded-md bg-surface border border-border text-sm text-txt-primary outline-none focus:border-brand-500"
                        />
                        <span className="text-txt-tertiary text-xs">until</span>
                        <select
                          value={w.ed}
                          onChange={e => { const ws = [...form.recurrence.windows]; ws[i] = { ...ws[i], ed: Number(e.target.value) }; setForm({ ...form, recurrence: { ...form.recurrence, windows: ws } }) }}
                          className="px-2 py-1.5 rounded-md bg-surface border border-border text-sm text-txt-primary outline-none focus:border-brand-500"
                        >
                          {DAY_NAMES.map((d, di) => <option key={di} value={di}>{d}</option>)}
                        </select>
                        <input
                          type="time"
                          value={w.et}
                          onChange={e => { const ws = [...form.recurrence.windows]; ws[i] = { ...ws[i], et: e.target.value }; setForm({ ...form, recurrence: { ...form.recurrence, windows: ws } }) }}
                          className="px-2 py-1.5 rounded-md bg-surface border border-border text-sm text-txt-primary outline-none focus:border-brand-500"
                        />
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, recurrence: { ...form.recurrence, windows: form.recurrence.windows.filter((_, j) => j !== i) } })}
                          className="p-1 rounded-md text-txt-tertiary hover:text-error hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          title="Remove window"
                        >
                          <XIcon />
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => setForm({ ...form, recurrence: { ...form.recurrence, windows: [...form.recurrence.windows, { sd: 5, st: '17:00', ed: 1, et: '09:00' }] } })}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-600 hover:underline"
                  >
                    <PlusIcon /> Add window
                  </button>

                  <div className="flex items-center gap-2">
                    <label className="text-xs text-txt-secondary">Timezone</label>
                    <select
                      value={form.recurrence.tz}
                      onChange={e => setForm({ ...form, recurrence: { ...form.recurrence, tz: e.target.value } })}
                      className="px-2 py-1.5 rounded-md bg-surface border border-border text-sm text-txt-primary outline-none focus:border-brand-500"
                    >
                      {TZ_OPTIONS.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                    </select>
                  </div>
                  <div className="rounded-md bg-surface px-3 py-2 border border-border">
                    <span className="text-xs font-medium text-txt-secondary">Active </span>
                    <span className="text-xs text-txt-primary">{summarizeWindows(form.recurrence.windows)}</span>
                  </div>
                </div>
              )}

              {/* Preview */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">Preview</label>
                <div className={`rounded-lg px-4 py-3 text-sm ${
                  form.style === 'info'
                    ? 'bg-blue-50 text-blue-800 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800'
                    : form.style === 'warning'
                    ? 'bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800'
                    : 'bg-red-50 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800'
                }`}>
                  <span className="font-medium">{form.title || 'Notice title'}</span>
                  <span className="mx-1.5">—</span>
                  <span>{form.body || 'Notice message will appear here...'}</span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border flex-shrink-0">
              <button
                onClick={() => { setShowForm(false); setEditingNotice(null) }}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-txt-secondary hover:bg-surface-hover transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors duration-150 shadow-sm"
              >
                {editingNotice ? 'Save Changes' : 'Create Notice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
